import React, { ChangeEvent, useEffect } from 'react';
import styles from './Header.module.scss';
import { NavLink, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    setSearch,
    setMobileSearchVisibility,
    setMobileSideBarVisibility,
    useAppStateHeaderSelector,
} from './headerSlice';
import { HeaderAppState } from './headerAppState';
import { Dropdown } from '../dropdown/Dropdown';
import { useTranslation } from 'react-i18next';
import { HeaderUserDropdown } from './HeaderUserDropdown';
import { loadLanguageOptions, setLanguage } from './headerActionCreators';
import { loadMessages } from '../../features/messages/messagesActionCreators';
import { useAuthSelector } from '../../features/auth/authSlice';
import { SiteSearch } from '../siteSearch/SiteSearch';
import { InputOnChangeData } from 'semantic-ui-react';

export interface HeaderProps {
    isSignedIn: boolean;
}

export function Header(props: HeaderProps) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const state: HeaderAppState = useAppStateHeaderSelector((state) => state);
    const {
        mobileSearchVisibility,
        mobileSideBarVisibility,
        search,
        languageOptions,
        selectedLanguage,
    } = state;
    const authUser = useAuthSelector((state) => state.authUser);

    useEffect(() => {
        dispatch(loadLanguageOptions());
        if (authUser) {
            dispatch(loadMessages());
        }
        console.log('[DIAG] Header mounted/updated - loadLanguageOptions dispatched, authUser:', authUser ? { Id: (authUser as any).Id, Email: (authUser as any).Email } : null);
    }, [dispatch, authUser]);

    const changeLanguage = (e: any, data: any) => {
        // Only change language if a different language is actually selected
        if (data.value && data.value !== selectedLanguage) {
            console.log('Language changed from', selectedLanguage, 'to', data.value);
            i18n.changeLanguage(data.value);
            dispatch(setLanguage(data.value, true));
        }
    };

    const history = useHistory();

    const onSearch = () => {
        history.push(`/search?term=${search}`);
        dispatch(setSearch(''));
    };

    const handleSearchChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        data: InputOnChangeData,
    ) => {
        dispatch(setSearch(data.value));
    };

    return (
        <>
            <header className={`${styles.headerWeb} ${styles.menu}`} role="banner">
                <div className={styles.headerTopContainer}>
                    <nav className={styles.menuLeftContainer} aria-label={t('Header.MainNavigation', 'Main navigation')}>
                        <a
                            href="https://www.cityofelgin.org/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.menuItem}
                            aria-label={t('Menu.CityOfElginHomepage', 'City of Elgin Homepage')}
                        >
                            <div className={styles.menuLogo} role="img" aria-hidden="true"></div>
                            <span className={styles.visuallyHidden}>{t('Menu.CityOfElginHomepage', 'City of Elgin Homepage')}</span>
                        </a>
                        <NavLink exact to="/" className={styles.menuItem}>
                            {t('Menu.Home')}
                        </NavLink>
                        <a
                            href="https://www.cityofelgin.org/59/Residents"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.menuItem}
                        >
                            {t('Menu.Resident', { count: 2 })}
                        </a>
                        <a
                            href="https://www.cityofelgin.org/35/Doing-Business"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.menuItem}
                        >
                            {t('Menu.Business', { count: 2 })}
                        </a>
                        <a
                            href="https://www.cityofelgin.org/76/Government"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.menuItem}
                        >
                            {t('Menu.Government')}
                        </a>
                        <a
                            href="https://www.cityofelgin.org/31/Explore-Elgin"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.menuItem}
                        >
                            {t('Menu.ExploreElgin')}
                        </a>
                    </nav>
                    <div className={styles.headerSearchContainer}>
                        <SiteSearch
                            onChange={handleSearchChange}
                            className={styles.searchInput}
                            value={search}
                            onSearch={onSearch}
                        />
                    </div>
                    <div className={styles.menuRightContainer}>
                        {props.isSignedIn ? (
                            <div
                                className={`${styles.menuItem} ${styles.dropdown}`}
                            >
                                <HeaderUserDropdown />
                            </div>
                        ) : (
                            <>
                                <NavLink
                                    to="/login"
                                    className={styles.menuItem}
                                >
                                    {t('Login.Login')}
                                </NavLink>
                                <div
                                    className={`${styles.menuItem} ${styles.dropdown}`}
                                >
                                    <Dropdown
                                        selection={false}
                                        fluid={false}
                                        value={selectedLanguage}
                                        search={false}
                                        staticText={t('SideBar.Language')}
                                        placeholder={t('SideBar.Language')}
                                        options={languageOptions}
                                        onChange={changeLanguage}
                                    />
                                </div>
                            </>
                        )}
                        <button
                            type="button"
                            className={`${
                                styles.menuHiddenWebSearchContainer
                            } ${
                                mobileSearchVisibility
                                    ? styles.mobileSearchVisible
                                    : ''
                            }`}
                            onClick={() =>
                                dispatch(
                                    setMobileSearchVisibility({
                                        value: !mobileSearchVisibility,
                                    }),
                                )
                            }
                            aria-expanded={mobileSearchVisibility}
                            aria-label={mobileSearchVisibility ? t('Header.CloseSearch', 'Close search') : t('Header.OpenSearch', 'Open search')}
                        >
                            <div className={styles.headerSearchContainer}>
                                <i
                                    className={`${styles.mobileSearchButtonToggle} search icon`}
                                    aria-hidden="true"
                                />
                            </div>
                        </button>
                    </div>
                </div>
                {mobileSearchVisibility && (
                    <div className={styles.headerBottomContainer}>
                        <SiteSearch
                            onChange={handleSearchChange}
                            placeholder="Search..."
                            onSearch={onSearch}
                            value={search}
                        />
                    </div>
                )}
            </header>
            <header className={`${styles.headerMobile} ${styles.menu}`} role="banner">
                <div className={styles.headerMobileTopContainer}>
                    <button
                        type="button"
                        className={styles.menuMobileLeftContainer}
                        onClick={() =>
                            dispatch(
                                setMobileSideBarVisibility({
                                    value: !mobileSideBarVisibility,
                                }),
                            )
                        }
                        aria-expanded={mobileSideBarVisibility}
                        aria-label={mobileSideBarVisibility ? t('Header.CloseMenu', 'Close menu') : t('Header.OpenMenu', 'Open menu')}
                    >
                        <i
                            className={`${styles.headerHamburger} bars icon`}
                            aria-hidden="true"
                        ></i>
                    </button>
                    <div className={styles.menuMobileLogoContainer}>
                        <div className={styles.menuMobileLogo} role="img" aria-label={t('Menu.CityOfElginLogo', 'City of Elgin Logo')}></div>
                    </div>
                    <button
                        type="button"
                        className={`${styles.menuMobileRightContainer} ${
                            mobileSearchVisibility
                                ? styles.mobileSearchVisible
                                : ''
                        }`}
                        onClick={() =>
                            dispatch(
                                setMobileSearchVisibility({
                                    value: !mobileSearchVisibility,
                                }),
                            )
                        }
                        aria-expanded={mobileSearchVisibility}
                        aria-label={mobileSearchVisibility ? t('Header.CloseSearch', 'Close search') : t('Header.OpenSearch', 'Open search')}
                    >
                        <div className={styles.headerSearchContainer}>
                            <i
                                className={`${styles.mobileSearchButtonToggle} search icon`}
                                aria-hidden="true"
                            />
                        </div>
                    </button>
                </div>
                <div
                    className={styles.headerMobileBottomContainer}
                    style={{
                        display: mobileSearchVisibility ? 'flex' : 'none',
                    }}
                >
                    <SiteSearch
                        onChange={handleSearchChange}
                        className={`${styles.searchInput} ${styles.mobileSearchInput}`}
                        placeholder="Search..."
                        onSearch={onSearch}
                        value={search}
                    />
                </div>
            </header>
        </>
    );
}
